/**
 * MCG Commercial LED Uploader Specifications
 * 
 * Reuses the same specs as club LED uploads but:
 * - Different Make.com webhook (routes to commercial Airtable base)
 * - Excludes Ponsford Stage (commercial doesn't have access)
 * - Tiled & 360 enforce exact 30s duration (commercial slot length)
 * 
 * Supports:
 * - Tiled: 4 levels, 1 file each
 * - 360: 4 levels, multiple segments each
 * - Interchange: Single asset for all levels
 */

// Import base specs from club LED config
import {
  TILED_SPECS as BASE_TILED_SPECS,
  THREE_SIXTY_SPECS as BASE_THREE_SIXTY_SPECS,
  INTERCHANGE_SPEC,
  // Helpers
  getSegmentIdFromFilename,
  validateFilename,
  getDimensionsForFile,
  buildSpecLabel,
  formatDuration,
} from "./mcgLedSpecs";

// ============================================================
// COMMERCIAL DURATION OVERRIDE
// Tiled & 360 use exact 30s for commercial slots
// ============================================================
const COMMERCIAL_DURATION = 30;

function withExactDuration(specs) {
  const result = {};
  for (const [key, spec] of Object.entries(specs)) {
    const { durationRange, ...rest } = spec;
    result[key] = { ...rest, exactDuration: COMMERCIAL_DURATION };
  }
  return result;
}

const TILED_SPECS = withExactDuration(BASE_TILED_SPECS);
const THREE_SIXTY_SPECS = withExactDuration(BASE_THREE_SIXTY_SPECS);

// ============================================================
// LED FORMAT OPTIONS (excludes Ponsford for commercial)
// ============================================================
export const LED_FORMATS = [
  { id: "tiled", label: "Tiled" },
  { id: "360", label: "360" },
  { id: "interchange", label: "Interchange" },
];

// ============================================================
// ALL SPECS BY FORMAT (no Ponsford)
// ============================================================
export const SPECS_BY_FORMAT = {
  tiled: TILED_SPECS,
  "360": THREE_SIXTY_SPECS,
  interchange: INTERCHANGE_SPEC,
};

// ============================================================
// ALL SPECS (flat object for lookup, no Ponsford)
// ============================================================
export const ALL_SPECS = {
  ...TILED_SPECS,
  ...THREE_SIXTY_SPECS,
  ...INTERCHANGE_SPEC,
};

// ============================================================
// METADATA WEBHOOK (Commercial - routes to commercial Airtable base)
// ============================================================
export const METADATA_WEBHOOK = "https://hook.eu2.make.com/fgninplw8emfjkontq8csdhjxxc2g00v";

// ============================================================
// RE-EXPORT HELPERS
// ============================================================
export {
  TILED_SPECS,
  THREE_SIXTY_SPECS,
  getSegmentIdFromFilename,
  validateFilename,
  getDimensionsForFile,
  buildSpecLabel,
  formatDuration,
};

// ============================================================
// DEFAULT EXPORT
// ============================================================
const mcgCommercialLedSpecs = {
  LED_FORMATS,
  SPECS_BY_FORMAT,
  ALL_SPECS,
  METADATA_WEBHOOK,
  getSegmentIdFromFilename,
  validateFilename,
  getDimensionsForFile,
  buildSpecLabel,
  formatDuration,
};

export default mcgCommercialLedSpecs;
